<h2 class="title">{{ 'pipeline_category_editor.title' | translate }}</h2>

<div class="loading-row" *ngIf="facade.loading$ | async" data-cy="pipeline-category-loading">
  <mat-progress-spinner diameter="16" mode="indeterminate"></mat-progress-spinner>
  <span>{{ 'pipeline_category_editor.loading' | translate }}</span>
</div>

<div class="field" *ngIf="!(facade.loading$ | async)" data-cy="pipeline-category-name-field">
  <label class="field-label" for="pipeline-category-name-input">
    {{ 'pipeline_category_editor.category_name_label' | translate | uppercase }}
  </label>
  <div class="input-wrap" cdkOverlayOrigin #categoryOrigin="cdkOverlayOrigin" #categoryWrap>
    <input
      #categoryInput
      id="pipeline-category-name-input"
      class="ts-input"
      type="text"
      [formControl]="categoryControl"
      [placeholder]="'pipeline_category_editor.category_name_placeholder' | translate"
      (focus)="onCategoryFocus()"
      (blur)="onCategoryBlur()"
      (keydown.enter)="onCategoryEnter(); $event.preventDefault()"
      (keydown.arrowDown)="onCategoryArrowDown($event)"
      (keydown.arrowUp)="onCategoryArrowUp($event)"
      (keydown.escape)="onCategoryEscape()"
      data-cy="pipeline-category-name-input"
    />
    <span
      class="category-uuid"
      *ngIf="facade.category$ | async as category"
      [class.category-uuid--with-button]="!(facade.readonly$ | async)"
      data-cy="pipeline-category-uuid"
    >
      {{ category.uuid }}
    </span>
    <button
      *ngIf="(facade.category$ | async) && !(facade.readonly$ | async)"
      type="button"
      class="translate-icon-button"
      (click)="onCategoryTranslateClicked()"
      data-cy="category-translate"
    >
      <i class="ri-translate-2"></i>
    </button>
  </div>

  <ng-template
    cdkConnectedOverlay
    [cdkConnectedOverlayOrigin]="categoryOrigin"
    [cdkConnectedOverlayOpen]="isDropdownOpen && categorySearchTerm.length > 0"
    [cdkConnectedOverlayWidth]="categoryWrap.offsetWidth"
    [cdkConnectedOverlayPositions]="suggestionsOverlayPositions"
    [cdkConnectedOverlayFlexibleDimensions]="true"
    [cdkConnectedOverlayPush]="true"
  >
    <ul class="suggestions" tsInfiniteScroll (reachedBottom)="onCategoryReachedBottom()">
      <li
        class="suggestion"
        *ngFor="let label of categorySuggestions; let i = index; trackBy: trackByUuid"
        [class.suggestion--highlighted]="highlightedIndex === i"
        (mousedown)="$event.preventDefault()"
        (click)="onCategoryPicked(label)"
        data-cy="pipeline-category-suggestion"
      >
        <span class="suggestion__value">{{ matchedValue(label, categorySearchTerm) }}</span>
        <span class="suggestion__lang">({{ matchedLanguage(label, categorySearchTerm) }})</span>
        <span class="suggestion__uuid">{{ label.uuid }}</span>
      </li>
      <li
        class="suggestion suggestion--loading"
        *ngIf="facade.loadingLabels$ | async"
        data-cy="pipeline-category-suggestions-loading"
      >
        <mat-progress-spinner diameter="20" mode="indeterminate"></mat-progress-spinner>
      </li>
      <li
        class="suggestion suggestion--create"
        [class.suggestion--highlighted]="highlightedIndex === categorySuggestions.length"
        (mousedown)="$event.preventDefault()"
        (click)="onCategorySubmitFreeText()"
      >
        <i class="ri-add-line suggestion__icon" aria-hidden="true"></i>
        <span>{{ 'pipeline_category_editor.create_new' | translate:{ term: categorySearchTerm } }}</span>
      </li>
    </ul>
  </ng-template>
  <div
    class="field-error"
    *ngIf="facade.categoryFieldError$ | async"
    data-cy="pipeline-category-name-error"
  >
    {{ 'pipeline_category_editor.category_name_required' | translate }}
  </div>
</div>

<div
  class="field"
  *ngIf="facade.showOptionsSection$ | async"
  @collapse
  data-cy="pipeline-category-options-field"
>
  <ts-category-options-management-list
    [items]="(facade.optionsForDisplay$ | async) ?? []"
    [categoryName]="(facade.categoryDisplayName$ | async) ?? ''"
    [canEdit]="!(facade.readonly$ | async)"
    [colorActionVisible]="(facade.supportsLabelColors$ | async) ?? true"
    [loading]="(facade.loading$ | async) ?? false"
    [searchTerm]="(facade.optionInput$ | async) ?? ''"
    [suggestions]="(facade.suggestions$ | async) ?? []"
    [suggestionsLoading]="(facade.loadingLabels$ | async) ?? false"
    (searchTermChanged)="onOptionSearchTermChanged($event)"
    (optionPicked)="onOptionPicked($event)"
    (freeTextSubmitted)="onOptionFreeTextSubmitted($event)"
    (focused)="onOptionFocused($event)"
    (blurred)="onOptionBlurred()"
    (thresholdClicked)="onOptionThresholdClicked($event)"
    (colorClicked)="onOptionColorClicked($event)"
    (translateClicked)="onOptionTranslateClicked($event)"
    (removeConfirmed)="onOptionRemoveConfirmed($event)"
    (reordered)="onOptionsReordered($event)"
    (loadMoreRequested)="onOptionReachedBottom()"
  ></ts-category-options-management-list>
</div>

<div class="error" *ngIf="(facade.error$ | async) as errorKey" data-cy="pipeline-category-error">
  {{ errorKey | translate }}
</div>

<div class="buttons-row">
  <button type="button" class="btn btn-outline-primary" (click)="onCancel()" data-cy="pipeline-category-cancel">
    <i class="ri-close-fill"></i>
    {{ 'button.cancel' | translate }}
  </button>
  <button
    type="button"
    class="btn btn-primary"
    [disabled]="!(canSubmit$ | async) || (facade.saving$ | async)"
    (click)="onSave()"
    data-cy="pipeline-category-save"
  >
    <i class="ri-save-line"></i>
    {{ 'button.continue' | translate }}
  </button>
</div>
